<template>
<div>

	<slot name="top"></slot>

	<div v-if="showLoadingScreen" class="text-center">
		Loading...
	</div>
	<div v-else-if="invoiceExists == false && isForSingleService" class="card" style="margin-bottom: 15px;">
		<div class="card-body text-center">
			<template v-if="!generatingInvoice && !isQuote">
				<p style="font-size: 18pt">There is no Invoice generated yet for this Service. Would you like to create one?</p>

				<p class="text-muted">This will pull the information from the service and copy it into an invoice record for editing.</p>

				<button class="btn btn-primary btn-lg" v-on:click="clickGenerateInvoice">
					GENERATE INVOICE
				</button>
			</template>
			<template v-if="!generatingInvoice && isQuote">
				
				<p style="font-size: 18pt">There is no Quote created yet for this Service. Would you like to create one?</p>

				<button class="btn btn-primary btn-lg" v-on:click="createQuote">
					CREATE QUOTE
				</button>

				<div class="mt-3">
					<button class="btn btn-light" v-on:click="copyFromQuoteTemplate">
						<i class="fa fa-copy"></i>
						Copy from Quote Template
					</button>
				</div>
			</template>

			<p v-else style="font-size: 18pt">{{generatingInvoiceMsg}}</p>
		</div>
	</div>

	<collapsible-panel v-if="!showLoadingScreen && invoiceExists" 
		panel-size="sm" 
		:always-open="invoiceSlug != 'new'" :external-toggle="invoiceDetails.finalized == 0 && invoiceSlug != 'new'">
		
		<template slot="panel">
			<h5>{{typeName}} Sections</h5>

			<panel-menu
				:menu-items="invoiceSections"
				:active-item="activeSection"
				v-on:itemClick="activateSection">
			</panel-menu>
		</template>

		<template slot="main">
			<alert v-if="isForSingleService && !$store.service.completed && !isQuote" 
				class="alert-danger incomplete-alert" 
				:visible="true"
				:unclosable="true"
				style="margin-bottom: 10px">
				This service is not complete
			</alert>
			<alert 
				v-if="worksheetsChanged && !isQuote"
				class="alert-warning worksheet-change-alert" 
				:visible="invoiceChangedAlertVisible"
				v-on:close="invoiceChangedAlertVisible = false">
				<div class="d-flex flex-row align-items-center flex-wrap">
					<h5 style="width: 250px;margin-bottom: 0;"><i class="fa fa-exclamation-triangle"></i> Worksheet Changed</h5>
					<p style="margin-bottom: 0;">There have been changes in a Service worksheet since the invoice was generated. If you want these changes to reflect on the invoice, it must be regenerated.</p>
				</div>
			</alert>

			<div class="row">

				<div :class="{'col-xl-8 col-lg-6':!previewExpanded && invoiceSlug != 'new', 'col-xl-6 col-lg-12':previewExpanded && invoiceSlug != 'new','col-xl-12': invoiceSlug == 'new'}">
					<div v-if="invoiceGenerationComplete" class="alert alert-success text-center">
						<i class="fa fa-3x fa-check text-success"></i>

						<h5>Your {{typeName.toLowerCase()}} is ready for editing</h5>
						<p v-if="!isQuote && invoiceDetails.is_freeform == 0">All the data from the service(s) has been copied over, and you can now edit the invoice details before </p>
						<button class="btn btn-light" v-on:click="invoiceGenerationComplete = false">
							<i class="fa fa-check"></i>
							Ok, Dismiss
						</button>
					</div>

					<modal-dialog
						title="Regenerate Invoice"
						confirm-button-text="Yes, Regenerate"
						:modal-visible="regenerateConfirmModalVisible"
						v-on:closeModal="regenerateConfirmModalVisible = false"
						v-on:confirm="regenInvoice">
						Are you sure you want to regenerate the invoice? This will reset any changes you have made and pull in data from the Service(s) again.
					</modal-dialog>

					<div class="d-flex flex-row justify-content-between">

						<h2 v-if="isForSingleService && $store.service" class="mb-0">
							{{typeName}}: Service #{{ $store.service.job_num }}<br>
							<small class="text-muted">{{ $store.service.job_name }}</small>
						</h2>
						<h2 v-else-if="invoiceSlug == 'new'" class="mb-0">New Invoice</h2>
						<h2 v-else-if="invoiceDetails.standalone_num" class="mb-0">
							Invoice #{{ invoiceDetails.manual_invoice_number ? invoiceDetails.manual_invoice_number : invoiceDetails.standalone_num }}<br>
							<small v-if="'ServiceContract' in invoiceDetails && invoiceDetails.ServiceContract" class="text-muted">For Service Contract <router-link :to="'/service-contract/'+invoiceDetails.ServiceContract.slug+'/details'">#{{invoiceDetails.ServiceContract.contract_num}} ({{invoiceDetails.ServiceContract.contract_description}})</router-link></small>
						</h2>
						<h2 v-else-if="'Service' in invoiceDetails && invoiceDetails.Service" class="mb-0">
							Invoice #{{ invoiceDetails.manual_invoice_number ? invoiceDetails.manual_invoice_number : invoiceDetails.Service.job_num }}
						</h2>

						<context-menu v-if="invoiceSlug && invoiceSlug != 'new'"
							:menu-items="contextMenuItems"
							v-on:itemClick="contextMenuClick">
						</context-menu>
					</div>

					<div class="mt-3 mb-4">

						<invoice-details-section v-if="activeSection == typeName+' Details'"
							v-model="invoiceDetailsModel"
							:is-single-service="isForSingleService"
							:invoice-slug="invoiceSlug"
							:addable-services="invoiceableServices"
							:details-save-working="detailsSaveWorking"
							:type-name="typeName"
							:hide-regen-button="invoiceDetailsModel.is_freeform == 1"
							:is-finalized="invoiceDetails.finalized == 1"
							v-on:change="updateDetails"
							v-on:saveInvoice="saveInvoice">
						</invoice-details-section>

						<invoice-tasks-section v-if="activeSection == 'Tasks' || activeSection == 'Service Tasks'"
							:invoice-tasks="invoiceTasks"
							:invoice-id="invoiceDetails.id"
							:type-name="typeName"
							billing-document-object-resource="ServiceInvoiceObject"
							:hide-regen-button="invoiceDetailsModel.is_freeform == 1 || isQuote"
							:is-global-service-tasks="activeSection == 'Tasks' ? false : true"
							:is-finalized="invoiceDetails.finalized == 1"
							v-on:confirmRegen="confirmRegenSection('Task')"
							v-on:modelChanged="fetchInvoiceData">
						</invoice-tasks-section>

						<invoice-labour-section v-if="activeSection == 'Labour' && !isQuote"
							:invoice-labour="invoiceLabour"
							:invoice-id="invoiceDetails.id"
							:hide-regen-button="invoiceDetailsModel.is_freeform == 1"
							:material-labour-total="materialLabourTotal"
							:quote-labour-total="invoiceDetails.quote_labour_total"
							:is-finalized="invoiceDetails.finalized == 1"
							:client-rate-overrides="clientRateOverrides"
							v-on:modelChanged="fetchInvoiceData"
							v-on:confirmRegen="confirmRegenSection('Labour')">
						</invoice-labour-section>

						<quote-labour-section v-else-if="activeSection == 'Labour' && isQuote"
							:labour-quoted="invoiceLabour"
							:quote-id="invoiceDetails.id"
							:material-labour-total="materialLabourTotal"
							quote-object-resource="ServiceInvoiceObject"
							quote-resource="ServiceInvoice"
							:is-finalized="invoiceDetails.finalized == 1"
							v-on:modelChanged="fetchInvoiceData"
							v-on:confirmRegen="confirmRegenSection('Labour')">
						</quote-labour-section>

						<!--
						<invoice-materials-section v-if="activeSection == 'Materials' && !isQuote"
							:billing-document-materials="invoiceMaterials"
							:billing-document-id="invoiceDetails.id"
							:client-invoice-settings="clientInvoiceSettings"
							:hide-regen-button="invoiceDetailsModel.is_freeform == 1"
							billing-document-object-resource="ServiceInvoiceObject"
							v-on:modelChanged="fetchInvoiceData"
							v-on:confirmRegen="confirmRegenSection('Material')">
						</invoice-materials-section>

						<quote-material-section v-else-if="activeSection == 'Materials' && isQuote"
							:billing-document-materials="invoiceMaterials"
							:quote-assemblies="assemblies"
							:billing-document-id="invoiceDetails.id"
							:client-invoice-settings="clientInvoiceSettings"
							:hide-regen-button="invoiceDetailsModel.is_freeform == 1"
							billing-document-object-resource="ServiceInvoiceObject"
							v-on:materialLabourChange="materialLabourChange"
							v-on:modelChanged="fetchInvoiceData">
						</quote-material-section>
						-->

						<material-section v-else-if="activeSection == 'Materials'"
							:billing-document-materials="invoiceMaterials"
							:billing-document-assemblies="assemblies"
							:billing-document-id="billingDocumentId"
							parent-key="invoice_id"
							:client-invoice-settings="clientInvoiceSettings"
							:hide-regen-button="invoiceDetailsModel.is_freeform == 1"
							:is-quote-section="isQuote"
							:billing-document-object-resource="billingDocumentObjectResource"
							:is-finalized="invoiceDetails.finalized == 1"
							v-on:materialLabourChange="materialLabourChange"
							v-on:modelChanged="fetchInvoiceData"
							v-on:confirmRegen="confirmRegenSection('Material')">
						</material-section>

						<invoice-equipment-section  v-if="activeSection == 'Equipment'"
							:invoice-equipment="invoiceEquipment"
							:invoice-id="invoiceDetails.id"
							:type-name="typeName"
							:hide-regen-button="invoiceDetailsModel.is_freeform == 1"
							:is-finalized="invoiceDetails.finalized == 1"
							:invoice-object-resource="billingDocumentObjectResource"
							v-on:modelChanged="fetchInvoiceData"
							v-on:confirmRegen="confirmRegenSection('Equipment')">
						</invoice-equipment-section>

						<invoice-additional-charges-section v-if="activeSection == 'Additional Charges'"
							:invoice-additional-charges="invoiceAdditionalCharges"
							:invoice-id="invoiceDetails.id"
							:is-finalized="invoiceDetails.finalized == 1"
							:invoice-object-resource="billingDocumentObjectResource"
							v-on:modelChanged="fetchInvoiceData">
						</invoice-additional-charges-section>

						<invoice-attachments-section v-if="activeSection == 'Attachments'"
							:selected-files-prop="selectedFiles"
							:attached-wo-summaries-json="invoiceDetails.attached_wo_summaries_json"
							:invoice-id="invoiceDetails.id"
							:service-id="'service_id' in invoiceDetails ? invoiceDetails.service_id : false"
							:type-name="typeName"
							:is-finalized="invoiceDetails.finalized == 1"
							v-on:selectionChange="attachmentSelectionChange"
							v-on:attachedWoSummariesChange="attachedWoSummariesChange">
						</invoice-attachments-section>

						<invoice-display-settings v-if="activeSection == 'Display Settings'"
							:document-settings="invoiceDisplaySettings"
							:global-settings="globalInvoiceSettings"
							:customer-settings="customerInvoiceSettings"
							:is-for-service="true"
							:is-finalized="invoiceDetails.finalized == 1"
							layer="document"
							v-on:settingsChange="invoiceDisplaySettingsChange">
						</invoice-display-settings>

						<invoice-finalizing-section v-if="activeSection == 'Finalizing'"
							:invoice-details="invoiceDetails"
							:type-name="typeName"
							:hide-partial-payments="isQuote ? true : false"
							:is-finalized="invoiceDetails.finalized == 1"
							v-on:detailsChange="setInvoiceDetails"
							v-on:salesTaxToggle="salesTaxToggle">
						</invoice-finalizing-section>

						<div v-if="activeSection == 'Delivery'" class="mb-4">

							<invoice-delivery-section
								:invoice-details="invoiceDetails"
								:client-invoice-settings="clientInvoiceSettings"
								:has-attached-files="hasAttachedFiles"
								:customer="customer"
								:type-name="typeName"
								api-resource="ServiceInvoice"
								v-on:quoteStatusChange="quoteStatusChanged"
								v-on:deFinalize="toggleFinalizedStatus(false)"
								v-on:generateInvoice="clickGenerateInvoice">
							</invoice-delivery-section>

							<activity-log 
								:submitted-log-json="invoiceDetails.submitted_log_json"
								:seen-log-json="invoiceDetails.seen_log_json"
								:payments-json="invoiceDetails.payments_json"
								:type-name="typeName"
								:editing-log-json="invoiceDetails.editing_log_json">
							</activity-log>

						</div>
					</div>

					<template v-if="invoiceSlug && invoiceSlug != 'new'">
						<button v-if="activeSection != typeName+' Details'" class="btn btn-light float-left" v-on:click="prevStep">
							<i class="fa fa-chevron-left"></i> Previous Step
						</button>

						<button v-if="activeSection != 'Finalizing' && activeSection != 'Delivery'" class="btn btn-light float-right" v-on:click="nextStep">
							Next Step <i class="fa fa-chevron-right"></i>
						</button>
						<div v-else-if="activeSection == 'Finalizing'" class="float-right">
							<button class="btn btn-lg btn-primary float-right" v-on:click="toggleFinalizedStatus(true)">
								<i class="fa fa-lock"></i>
								Finalize {{typeName}}
							</button>

							<p>The {{typeName}} must be finalized to move onto the delivery step</p>
						</div>
					</template>

					<template v-if="!isQuote && (activeSection == 'Finalizing' || invoiceDetails.finalized == 1)">
						<div style="clear: both;"></div>

						<div class="mt-2 d-flex flex-row flex-wrap financial-summary">
							<info-box class="mr-1" :label="typeName+' Total'" :value="invoiceTotal" :dark="true"></info-box>
							<info-box class="mr-1" label="Total Costs" :value="Number('totalCosts' in serviceMutatedForReport ? (serviceMutatedForReport.totalCosts != null ? serviceMutatedForReport.totalCosts : 0) : 0).toCurrency()"></info-box>
							<info-box class="mr-1" label="Revenue" :value="revenue"></info-box>
							<info-box class="mr-1"  label="Gross Profit" :value="grossProfit"></info-box>
							<info-box v-if="invoiceDetails.amount_paid > 0 && invoiceDetails.is_paid == 0" 
								label="Partial Payments" 
								:value="invoiceDetails.amount_paid">
							</info-box>
						</div>
					</template>

					<div style="clear: both;"></div>

					<comment-thread v-if="invoiceSlug != 'new' && invoiceSlug != 'new-quote'"
						class="mb-4"
						:thread-id="invoiceDetails.message_thread_id"
						:parent-slug="invoiceSlug" 
						parent-model="ServiceInvoice"
						:thread-subject-reference="threadSubjectRef">
					</comment-thread>

					<modal-dialog
						:modal-visible="regenerateSectionConfirmModal.visible"
						title="Regenerate Section"
						:confirm-button-text="'Yes, Regenerate '+regenerateSectionConfirmModal.sectionName"
						v-on:confirm="executeRegenSection"
						v-on:closeModal="regenerateSectionConfirmModal.visible = false">

						Are you sure you want to regenerate the {{regenerateSectionConfirmModal.sectionName}} section? This will overwrite any changes you have made and pull the data fresh from the Service record(s).

					</modal-dialog>

				</div>
				<div v-if="invoiceSlug != 'new'" :class="{'col-xl-4 col-lg-6':!previewExpanded, 'col-xl-6 col-lg-12':previewExpanded}">
					
					<div v-if="showPreview">

						<a class="float-right" :href="invoicePreviewSrc" target="_blank">View in another tab</a>

						<h3>
							<i v-if="!previewExpanded" class="fa fa-chevron-left icon-button" title="Expand Preview Panel" v-on:click="togglePreviewSize"></i>
							<i v-else class="fa fa-chevron-right icon-button" title="Retract Preview Panel" v-on:click="togglePreviewSize"></i>
							Preview
						</h3>

						<div style="position: relative;">
							<div class="refresh-token-container text-center" style="position: absolute;width: 100%;height: 100%;z-index: 1">
								<p>Click the button below to view the invoice preview</p>
								<button class="btn btn-light" v-on:click="reloadInvoice">
									<i class="fa fa-sync"></i>
									Reload Invoice
								</button>
							</div>
							<pdf-js v-if="invoiceSlug" v-on:click="reloadInvoice" style="position: relative;z-index: 2" :url="invoicePreviewSrc" :page="1"></pdf-js>
						</div>
					</div>
					<div v-else style="height: 100%;" class="d-flex justify-content-center align-items-center">
						<spinner :inline="true"></spinner>
					</div>
				
				</div>
			</div>
		</template>

	</collapsible-panel>

	<modal-dialog
		:modal-visible="partialPaymentModal.visible"
		title="Make a Partial Payment"
		confirm-button-text="Make Payment"
		cancel-button-text="Cancel"
		:working="partialPaymentModal.working"
		v-on:closeModal="partialPaymentModal.visible = false"
		v-on:confirm="confirmPartialPayment">

		<field label="Amount of Partial Payment" 
			col-class="col-6"
			:params="{type:'number',step:0.01}"
			v-model="partialPaymentModal.payment_amount">
		</field>

	</modal-dialog>

	<modal-dialog
		:modal-visible="cloneQuoteModal.visible"
		title="Clone Quote to Template"
		confirm-button-text="Save"
		cancel-button-text="Cancel"
		v-on:closeModal="cloneQuoteModal.visible = false"
		v-on:confirm="saveToTemplate">

		<field label="Provide a short description of the quote" 
			col-class="stacked"
			v-model="cloneQuoteModal.quote_description">
		</field>

	</modal-dialog>

	<modal-dialog
		:modal-visible="deleteInvoiceConfirmModalVisible"
		:title="'Delete '+typeName"
		:confirm-button-text="'Yes, Delete '+typeName"
		cancel-button-text="Cancel"
		button-class="btn-outline-danger"
		v-on:closeModal="deleteInvoiceConfirmModalVisible = false"
		v-on:confirm="confirmDeleteInvoice">

		Are you sure you want to delete this {{ typeName.toLowerCase() }}?

	</modal-dialog>

	<modal-dialog v-if="invoiceDetails.finalized == 1 && !isQuote"
		:modal-visible="updateAmountDueModal.visible"
		title="Update Total Requested"
		confirm-button-text="Confirm"
		cancel-button-text="Cancel"
		v-on:closeModal="updateAmountDueModal.visible = false"
		v-on:confirm="confirmUpdateAmountDue">

		<table v-if="'amount_due' in invoiceDetails && newAmountDue !== null && newBalanceOwing !== null" class="table table-sm">
			<tbody>
				<tr>
					<th class="text-right" style=" border: none;">Requested to Date</th>
					<td class="text-right" style=" border: none;">{{ Number(invoiceDetails.amount_due).toCurrency() }}</td>
				</tr>
				<tr>
					<th class="text-right" style=" border: none;">Request Amount</th>
					<td class="text-right" style=" border: none;">
						<field
							:params="{
								type:'number',
								step:0.01,
								style: 'textAlign:right'
							}"
							v-model="updateAmountDueModal.amount_due">
						</field>
					</td>
				</tr>
				<tr>
					<th class="text-right" style=" border: none;">New Total Requested</th>
					<th class="text-right" style=" border: none;">{{ Number(newAmountDue).toCurrency() }}</th>
				</tr>
				<tr>
					<th class="text-right" style=" border: none;">New Balance Owing</th>
					<th class="text-right" style=" border: none;">{{ Number(newBalanceOwing).toCurrency() }}</th>
				</tr>
			</tbody>
		</table>

	</modal-dialog>

	<modal-dialog
		:modal-visible="invoiceRegenSourceModal.visible"
		title="Choose Data Source"
		cancel-button-text="Cancel"
		:submit-visible="false"
		v-on:closeModal="invoiceRegenSourceModal.visible = false">

		<p>Where do you want to pull the data from?</p>

		<div class="text-center">
			<button class="btn btn-light" type="button" v-on:click="generateInvoiceSectionFromQuote(acceptedQuote,invoiceRegenSourceModal.section)">
				Pull from Quote
			</button>
			<button class="btn btn-light" type="button" v-on:click="generateInvoiceSectionFromWorksheets(invoiceRegenSourceModal.section)">
				Pull from Worksheets
			</button>
		</div>

	</modal-dialog>

	<modal-dialog
		:modal-visible="confirmQBOPushModal.visible"
		title="Update Quickbooks?"
		cancel-button-text="No"
		confirm-button-text="Yes, Update Quickbooks"
		v-on:confirm="qboPushConfirmed"
		v-on:closeModal="qboPushRejected">

		This action changes the state of the invoice. Would you like Quickbooks Online to sync?

	</modal-dialog>

	<service-quote-template-picker-modal
		:modal-visible="templatesModalVisible"
		v-on:closeModal="templatesModalVisible = false"
		v-on:templateSelected="pickTemplateToCopy">
	</service-quote-template-picker-modal>

</div>
</template>
<script type="text/javascript">
import ServiceMixins from "@/mixins/ServiceMixins.js"
import InvoiceMixins from "@/mixins/InvoiceMixins.js"
import InvoiceMaterialMixins from "@/mixins/InvoiceMaterialMixins.js"
import ReportsMixins from "@/mixins/ReportsMixins.js"
import QBOUpdateMixins from "@/mixins/QBOUpdateMixins.js"
import ServiceQuoteTemplateMixins from "@/mixins/ServiceQuoteTemplateMixins.js"

import ContextMenu from "@/layout_fragments/ContextMenu"
import Alert from "@/components/Alert"
import ModalDialog from "@/components/ModalDialog"
import PanelMenu from "@/components/PanelMenu"
import CollapsiblePanel from "@/layout_fragments/CollapsiblePanel"
import ActivityLog from "@/components/ActivityLog"
import InfoBox from "@/components/InfoBox"
import Field from "@/components/Field"
import Spinner from "@/components/Spinner"

import InvoiceDetailsSection from "./invoicing/InvoiceDetailsSection"
import InvoiceTasksSection from "./invoicing/InvoiceTasksSection"
import InvoiceLabourSection from "./invoicing/InvoiceLabourSection"
import MaterialSection from "./invoicing/MaterialSection"
import InvoiceEquipmentSection from "./invoicing/InvoiceEquipmentSection"
import InvoiceAdditionalChargesSection from "./invoicing/InvoiceAdditionalChargesSection"
import InvoiceAttachmentsSection from "./invoicing/InvoiceAttachmentsSection"
import InvoiceFinalizingSection from "./invoicing/InvoiceFinalizingSection"
import InvoiceDeliverySection from "@/components/super_components/InvoiceDeliverySection"
import InvoiceDisplaySettings from "@/components/InvoiceDisplaySettings"

import QuoteLabourSection from "./invoicing/QuoteLabourSection"
//import QuoteMaterialSection from "./invoicing/QuoteMaterialSection"
import QuoteEquipmentSection from "./invoicing/QuoteEquipmentSection"

import PdfJs from "@/components/PdfJs"
import CommentThread from "@/components/super_components/CommentThread"
import ServiceQuoteTemplatePickerModal from "@/components/super_components/ServiceQuoteTemplatePickerModal"

export default {
	name: "InvoiceUi",
	components: {
		ModalDialog,
		PanelMenu,
		ContextMenu,
		Field,
		Alert,
		InfoBox,
		CollapsiblePanel,
		ActivityLog,
		InvoiceDetailsSection,
		InvoiceTasksSection,
		InvoiceLabourSection,
		MaterialSection,
		InvoiceEquipmentSection,
		InvoiceAdditionalChargesSection,
		InvoiceAttachmentsSection,
		InvoiceFinalizingSection,
		InvoiceDeliverySection,
		InvoiceDisplaySettings,
		QuoteLabourSection,
		//QuoteMaterialSection,
		QuoteEquipmentSection,
		PdfJs,
		CommentThread,
		Spinner,
		ServiceQuoteTemplatePickerModal
	},
	mixins: [ServiceMixins,InvoiceMixins,ReportsMixins,InvoiceMaterialMixins, QBOUpdateMixins, ServiceQuoteTemplateMixins],
	props: [
		"isForSingleService",
		"invoiceSlug"
	],
	data() {
		return {
			billingDocumentObjectResource: "ServiceInvoiceObject",
			showLoadingScreen: true,
			invoiceExists: false,
			generatingInvoice: false,
			generatingInvoiceMsg: "",
			invoiceDataIsSet: false,
			intialStuffFetched: false,
			fetchingInitialStuff: false,
			invoiceDetails: {},
			invoiceServices: [], //ServiceWorksheets must be included on service
			invoiceMaterials: [],
			invoiceLabour: [],
			invoiceTasks: [],
			invoiceEquipment: [],
			invoiceAdditionalCharges: [],
			invoiceObjItems: [],
			customer: {},
			customer_id: null,
			classificationGroups: [],
			clientInvoiceSettings: {},
			clientInvoiceOverrides: {},
			clientRateOverrides: [],
			beforeImages: [],
			afterImages: [],
			selectedFiles: [],
			previewExpanded: null,
			regenerateSectionConfirmModal: {
				visible: false,
				sectionName: "",
				confirmFunction: ""
			},
			activeSection: "",
			detailsSaveWorking: false,
			regenerateConfirmModalVisible: false,
			invoiceGenerationComplete: false,
			invoiceChangedAlertVisible: true,
			scrolledBelowHeader: false,
			previewRefresher: 1,
			showActivityLog: false,
			showAttachments: false,
			serviceMutatedForReport: {},
			materialLabourTotal: 0,
			partialPaymentModal: {
				visible: false,
				payment_amount: 0,
				working: false
			},
			invoiceableServices: [],
			invoiceDetailsModel: {
				work_completed_description: null,
				services: [],
				customer_id: null,
				is_freeform: false
			},
			cloneQuoteModal: {
				visible: false,
				quote_description: ""
			},
			invoiceRegenSourceModal: {
				visible: false,
				section: "All"
			},
			updateAmountDueModal: {
				visible: false,
				amount_due: 0
			},
			deleteInvoiceConfirmModalVisible: false,
			dataFetched: false,
			customerFetched: false,
			currentlyFetchedServiceJSON: "",
			showPreview: false,
			templatesModalVisible: false,
			selectedTemplateToCopy: null
		}
	},
	watch: {
		invoiceServices() {
			//copy service data into invoice
			let vm = this

			if (!this.invoiceDataIsSet) {
				this.fetchInvoiceData()

				this.bindSelectedFiles()
			}

			if (this.invoiceServices.length > 0 && this.invoiceServices[0] && "ServiceInvoice" in this.invoiceServices[0]) {
				vm.invoiceExists = true
			}

			if (!this.dataFetched) {
				this.fetchData(false)
			}
		},
		'$store.service'() {
			this.fetchInvoiceData()
			
			if (!this.customerFetched)
				this.fetchCustomer()
		},
		'$store.sessionToken'() {
			this.$forceUpdate()
		},
		invoiceId() {
			if (!this.intialStuffFetched && this.invoiceId && this.invoiceSlug)
				this.fetchInitialStuff()
		},
		invoiceSlug() {
			if (!this.intialStuffFetched && this.invoiceSlug) {
				this.fetchInitialStuff() 
				this.fetchInvoiceData()
			}
		}
	},
	computed: {
		billingDocumentId() {
			if (this.invoiceDetails && "id" in this.invoiceDetails)
				return this.invoiceDetails.id
			return null
		},
		customerInvoiceSettings() {
			let settings = {}

			try {
				settings = JSON.parse(this.customer.settings_json)
			}
			catch (e) {}

			return settings
		},
		invoiceDisplaySettings() {
			let settings = {}

			try {
				settings = JSON.parse(this.invoiceDetails.settings_override_json)
			}
			catch (e) {}
			return settings
		},
		acceptedQuote() {
			if (this.quoteRecord && this.quoteRecord.quote_status == 'Accepted')
				return this.quoteRecord

			return false
		},
		threadSubjectRef() {
			let details = ""
			if ("Service" in this.invoiceDetails && this.invoiceDetails.Service)
				details = " #"+this.invoiceDetails.Service.job_num

			return this.typeName+details
		},
		contextMenuItems() {
			let items = []

			if (this.invoiceDetails.finalized == 1) {

				if (!this.isQuote) {
					if (this.invoiceDetails.is_processed == 0) {
						items.push({
							icon: 'fa-archive',
							text: "Mark as Processed",
							clickFunction: "toggleProcessed",
							arg: true
						})
					}
					else {
						items.push({
							icon: 'fa-edit',
							text: "Mark as Un-Processed",
							clickFunction: "toggleProcessed",
							arg: false
						})	
					}

					if ((this.invoiceDetails.amount_due != this.invoiceDetails.amount_paid || this.invoiceDetails.amount_due == 0) && 
						this.invoiceDetails.amount_paid != this.invoiceDetails.grand_total && 
						!this.invoiceDetails.amount_paid &&
						(this.invoiceDetails.amount_due == this.invoiceDetails.grand_total || this.invoiceDetails.amount_due == 0)
						) {
						items.push({
							icon: 'fa-check',
							text: "Mark as Paid",
							clickFunction: "recordFullPayment",
							arg: true
						})
					}

					items.push({
						icon: 'fa-dollar-sign',
						text: "Partial Payment",
						clickFunction: "makePartialPayment"
					})

					items.push({
						icon: 'fa-dollar-sign',
						text: "Update Total Requested",
						clickFunction: "updateAmountDue"
					})

					/*
					items.push({
						icon: 'fa-credit-card',
						text: "Manage Payment Options",
						clickFunction: "setPaymentOptions"
					})

					else {
						items.push({
							icon: 'fa-dollar',
							text: "Mark as Un-Paid",
							clickFunction: "togglePaid",
							arg: false
						})
					}*/
				}

				items.push({
					icon: 'fa-lock-open',
					text: "De-finalize for Editing",
					clickFunction: "toggleFinalizedStatus",
					arg: false
				})
			}
			else {
				if (this.invoiceDetailsModel.is_freeform == 0 && !this.isQuote) {
					items.push({
						icon: 'fa-sync',
						text: 'Regenerate Invoice',
						clickFunction: "regenInvoice"
					})
				}
				
				items.push({
					icon: 'fa-trash',
					text: 'Delete '+this.typeName,
					clickFunction: "deleteInvoice",
					itemClass: "text-danger"
				})
			}

			if (this.isQuote) {
				items.push({
					icon: 'fa-copy',
					text: 'Clone to Template',
					clickFunction: 'cloneToTemplate',
				})

				items.push({
					icon: 'fa-copy',
					text: 'Clone Quote',
					clickFunction: 'cloneQuote',
				})
			}

			return items
		},
		invoiceId() {
			if ("id" in this.invoiceDetails)
				return this.invoiceDetails.id
			else
				return false
		},
		typeName() {
			if (this.isQuote)
				return "Quote"
			else
				return "Invoice"
		},
		maxRequestedAllowed() {
			return 1000
		},
		newAmountDue() {
			if (this.invoiceDetails && "amount_due" in this.invoiceDetails && this.updateAmountDueModal && "amount_due" in this.updateAmountDueModal) {
				return +this.invoiceDetails.amount_due + +this.updateAmountDueModal.amount_due
			}
			else
				return 0
		},
		newBalanceOwing() {
			return this.newAmountDue - +(this.invoiceDetails.amount_paid != null ? this.invoiceDetails.amount_paid : 0)
		},
		balanceOwing() {
			return +this.invoiceDetails.grand_total - +this.invoiceDetails.amount_paid
		},
		invoiceTotal() {
			if (this.invoiceDetails && "grand_total" in this.invoiceDetails)
				return parseFloat(this.invoiceDetails.grand_total).toCurrency()
			return 0
		},
		revenue() {
			if (this.invoiceDetails && "pretax_total" in this.invoiceDetails)
				return parseFloat(this.invoiceDetails.pretax_total).toCurrency()
			return 0
		},
		grossProfit() {
			const pretax = parseFloat(this.invoiceDetails.pretax_total)
			const costs = +(this.serviceMutatedForReport.totalCosts != null ? this.serviceMutatedForReport.totalCosts : 0)
			const profit = (Number.isFinite(pretax) ? pretax : 0) - costs
			return profit.toCurrency()
		},
		worksheetsChanged() {
			let vm = this
			let changed = false

			if ("Service" in this.invoiceDetails && this.invoiceDetails.service && "ServiceWorksheet" in this.invoiceDetails.Service) {
				this.invoiceDetails.Service.ServiceWorksheet.forEach(ws => {
					let wsChange = vm.moment(ws.updated_at)
					let invoiceGenerated = vm.moment(vm.invoiceDetails.created_at)

					if (wsChange.isAfter(invoiceGenerated))
						changed = true
				})
			}

			if ("GroupServices" in this.invoiceDetails && this.invoiceDetails.GroupServices) {
				this.invoiceDetails.GroupServices.forEach(service => {

					if ("ServiceWorksheet" in service && service.ServiceWorksheet) {
						service.ServiceWorksheet.forEach(ws => {
							let wsChange = vm.moment(ws.updated_at)
							let invoiceGenerated = vm.moment(vm.invoiceDetails.created_at)

							if (wsChange.isAfter(invoiceGenerated))
								changed = true
						})
					}
				})
			}

			return changed
		},
		invoiceSections() {
			let sections = [], vm = this

			/*if (!this.isForSingleService)
				sections.push("Sources")*/

			sections.push(this.typeName+" Details")

			this.invoiceServices.forEach(service => {
				if (sections.indexOf("Tasks") == -1 && service && "ServiceContract" in service && service.ServiceContract)
					sections.push("Tasks")
			})

			if (vm.$store.serviceTasks && vm.$store.serviceTasks.length > 0) 
				sections.push("Service Tasks")

			sections = sections.concat([
				'Materials',
				'Labour',
				'Equipment',
				'Additional Charges',
				'Attachments',
				'Display Settings',
			])

			if (this.invoiceDetails.is_freeform == 1) {//freeform invoices can't have attachments, because there is no job files tab to pull from
				let attachmentsSectionIndex = sections.findIndex(sec => sec == 'Attachments')
				sections.splice(attachmentsSectionIndex,1)
			}

			if (this.invoiceDetails.finalized == 1) 
				sections.push("Delivery")
			else
				sections.push('Finalizing')
		
			return sections
		
		},
		timeLogParams() {
			let filters = []

			if (!this.isForSingleService) {
				filters = [
					["service_id",this.invoiceServices.map(service => service.id)]
				]

				//saveguard against invoiceServices being empty, and querying the entire database
				if (this.invoiceServices.length == 0) {
					filters.push(["service_id",0])
					filters.push(["job_id",0])
				}
			}
			else {
				filters = [
					["service_id",this.$store.service.id],
					["job_id",0]
				]
			}

			return {
				filters,
				include: [{
					model: "User", 
					fields: ["id","slug","first_name","last_name","classification_id"],
					include: [{
						model: "EmployeeClassification",
						include:["ClassificationGroup"]
					}]
				}]
			}
		},
		invoicePreviewSrc() {
			return window.apiBase+"ServiceInvoice/pdf?slug="+this.invoiceSlug+"&token="+this.$store.sessionToken+"&humanReadable=true"+"&v="+this.previewRefresher
		},
		hasAttachedFiles() {
			if (this.selectedFiles.length > 0 || this.invoiceDetails.attached_wo_summaries_json.length > 2)
				return true
			else
				return false
		}
	},
	methods: {
		updateDetails(details) {
			Object.keys(details).forEach(key => {
				if (key in this.invoiceDetails)
					this.invoiceDetails[key] = details[key]
			})

			if ("services" in details && details.services && details.services.length > 0) {
				this.invoiceDetails.services = details.services
				this.invoiceDetailsModel.services = details.services
			}

			if ("is_freeform" in details)
				this.invoiceDetailsModel.is_freeform = details.is_freeform

			if ('customer_id' in this.invoiceDetails && this.invoiceDetails.customer_id)
				this.fetchCustomer()

			if (this.invoiceSlug && this.invoiceSlug != 'new')
				this.executeSaveInvoice()
		},
		setInvoiceDetailsModel() {
			if ("work_completed_description" in this.invoiceDetails) {
				this.invoiceDetailsModel.work_completed_description = this.invoiceDetails.work_completed_description
			}
			if ("manual_invoice_number" in this.invoiceDetails)
				this.invoiceDetailsModel.manual_invoice_number = this.invoiceDetails.manual_invoice_number
		},
		setInvoiceDetails(details) {

			let vm = this
			if (!this.isForSingleService && Array.isArray(details.GroupServices)) {
				this.invoiceDetailsModel = {
					services: details.GroupServices.map(service => service.id),
					is_freeform: details.is_freeform,
					customer_id: details.customer_id,
					manual_invoice_number: details.manual_invoice_number
				}

				if ("work_completed_description" in details)
					this.invoiceDetailsModel.work_completed_description = details.work_completed_description

				if (!this.customer_id && details.GroupServices.length > 0) 
					this.customer_id = details.GroupServices[0].customer_id

				if (this.invoiceDetailsModel.services.length > 0) {
					this.fetchInvoiceServices({service_ids:this.invoiceDetailsModel.services})
				} else if (this.invoiceDetailsModel.is_freeform == 1 && 'Customer' in details && details.Customer) {
					this.setCustomer(details.Customer)
				}
			}
			else 
				this.invoiceServices = [details.Service]

			if ("is_freeform" in details)
				this.invoiceDetailsModel.is_freeform = details.is_freeform

			vm.showLoadingScreen = false

			Object.keys(details).forEach(key => {
				vm.$set(vm.invoiceDetails,key,details[key])
			})

			this.setInvoiceDetailsModel()

			//hack to get tinymce to re-bind itself
			if (this.activeSection == this.typeName+' Details' && "tinymce" in window && tinymce && "activeEditor" in tinymce && tinymce.activeEditor)
				tinymce.activeEditor.setContent(details.work_completed_description)
		},
		setCustomer(customer) {
			if (customer) {
				this.customer = customer
				this.customer_id = customer.id
        		this.invoiceDetails.Customer = customer

				this.clientRateOverrides = []
				try {
					this.clientRateOverrides = JSON.parse(customer.charge_out_group_overrides_json)
				}
				catch (ex) {}
			}
			else {
				this.customer = {}
				this.customer_id = null

			}
		},
		reloadInvoice() {
			let vm = this
			this.setSessionToken().then(response => {
				vm.previewRefresher++
			})
		},
		invoiceDisplaySettingsChange(settings) {
			let vm = this

			vm.$nextTick(() => {
				let payload = {
					slug: this.invoiceSlug,
					settings_override_json: JSON.stringify(settings),
					skipQBOPush: true
				}

				Object.keys(settings).forEach(key => {
					vm.clientInvoiceSettings[key] = settings[key]
				})

				this.postData("ServiceInvoice/save",payload).then(response => {
					if (response.success)
						vm.previewRefresher++
				})
			})
		},
		attachedWoSummariesChange(value) {
			this.invoiceDetails.attached_wo_summaries_json = value
		},
		customerChange(customer_id) {
			this.customer_id = customer_id
		},
		quoteStatusChanged(status) {
			this.fetchService()
		},
		permitCostChanged(cost) {

		},
		fetchInitialStuff() {
			let vm = this

			if (!this.fetchingInitialStuff) {
				this.spinner(true)
				this.fetchingInitialStuff = true

				let promises = [
					vm.fetchData(false),
					vm.fetchServiceTasks()
				]

				if (vm.isForSingleService) {
					vm.$set(vm.$store,"service",{})
					promises.push(this.fetchService())
				}

				if (this.invoiceSlug)
					promises.push(vm.fetchInvoiceData(false))

				Promise.all(promises).then(responses => {
					vm.spinner(false)

					vm.intialStuffFetched = true
					vm.fetchingInitialStuff = false

					if (vm.isForSingleService)  {
						vm.serviceMutatedForReport = vm.mutateService(responses[2].data)
					}
					else {
						vm.fetchInvoiceableServices()
					}

					if (vm.invoiceDetails.finalized == 1 && !vm.activeSection)
						vm.activeSection = "Delivery"
					else {
						if (vm.isQuote)
							vm.activeSection = "Quote Details"
						else
							vm.activeSection = "Invoice Details"
					}

					vm.fetchCustomer()

					if (!vm.invoiceSlug)
						vm.showLoadingScreen = false

				}).catch(response => {
					vm.spinner(false)
					vm.fetchingInitialStuff = false
					vm.showLoadingScreen = false
				})
			}
		},
		contextMenuClick(clickFunction,item) {
			if ("arg" in item)
				this[clickFunction](item.arg)
			else
				this[clickFunction]()
		},
		cloneToTemplate() {
			this.cloneQuoteModal.visible = true
			this.cloneQuoteModal.quote_description = ""
		},
		saveToTemplate() {
			let vm = this

			let payload = {}

			let params = {
				include: [{
					model: "ServiceInvoice",
					include: ["ServiceInvoiceObject"]
				}]
			}

			this.getJSON("Service/find/"+this.invoiceDetails.service_id,params).then(response => {
				if (response.success) {
					let fieldsToCopy = [
						"category_id",
						"revenue_code_id",
						"permit_needed",
						"permit_cost",
						"has_locates",
						"pays_mileage",
						"pays_perdiem"
					]

					fieldsToCopy.forEach(field => {
						payload[field] = response.data[field]
					})

					let quote = false
					if ("ServiceInvoice" in response.data && response.data.ServiceInvoice)
						quote = response.data.ServiceInvoice.find(inv => inv.is_quote == 1)

					payload.work_to_do = quote.work_completed_description
					payload.quote_description = vm.cloneQuoteModal.quote_description

					vm.postData("ServiceQuoteTemplate/save",payload,true).then(saveResponse => {
						if (saveResponse.success) {

							let templateObjects = []
							if (quote && quote.ServiceInvoiceObject) {
								templateObjects = quote.ServiceInvoiceObject.map(item => {
									let values = {}
									try {
										values = JSON.parse(item.field_values_json)
									}
									catch (ex) {}

									if (item.type == "Material") {
										return {
											quote_template_id: saveResponse.data.record_id,
											material_id: values.material_id,
											qty: values.qty
										}
									}
									else if (item.type == "Equipment") {
										return {
											quote_template_id: saveResponse.data.record_id,
											equipment_type_id: values.equipment_type_id,
											qty: values.qty,
											unit: values.unit
										}
									}
									else if (item.type == "Labour") {
										return {
											quote_template_id: saveResponse.data.record_id,
											group_id: values.group_id,
											qty: values.hrs
										}
									}
									else if (item.type == "Additional Charges") {
										return {
											quote_template_id: saveResponse.data.record_id,
											group_id: values.group_id,
											qty: "qty" in values ? values.qty : 0
										}
									}
									else if (item.type == "Tasks") {
										return {
											quote_template_id: saveResponse.data.record_id,
											group_id: values.group_id,
											qty: "qty" in values ? values.qty : 0
										}
									}
								})
							}
							let objectPayload = {
								records: JSON.stringify(templateObjects)
							}

							vm.postData("ServiceQuoteTemplateObject/save-batch",objectPayload).then(response => {
								vm.alertSaved("Quote saved as a template!")
								vm.cloneQuoteModal.visible = false
							})
						}
					})
				}
			})
		},
		cloneQuote() {
			let vm = this

			vm.spinner(true)

			let service = {}

			this.getJSON("Service/find/"+this.invoiceDetails.service_id).then(response => {
				if (response.success) {
					let payload = vm.cloneObj(response.data)

					let deleteFields = [
						"status",
						"completed",
						"archived",
						"id",
						"slug",
						"job_num",
						"tenant_id",
						"total_material_costs",
						"total_equipment_costs",
						"total_service_task_costs",
						"total_labour_costs",
						"summary_total_hours",
						"summary_total_hours_ot",
					]

					deleteFields.forEach(field => {
						if (field in payload)
							delete payload[field]
					})

					if (!("check_in_tolerance" in payload) || !payload.check_in_tolerance)
						payload.check_in_tolerance = 200

					let promises = [
						vm.postData("Service/save",payload,true),
						vm.getJSON("ServiceInvoice/find/"+vm.invoiceDetails.slug),
						vm.getJSON("ServiceInvoiceObject/get",{
							filters: [["invoice_id",vm.invoiceDetails.id]]
						})
					]

					Promise.all(promises).then(responses => {
						let allSuccess = true
						responses.forEach(res => {
							if (!res.success)
								allSuccess = false
						})

						if (allSuccess) {

							let quotePayload = {
								service_id: responses[0].data.record_id,
								is_quote: 1
							}

							let copyFields = [
								"work_completed_description",
								"internal_notes",
								"grand_total",
								"untaxed_total",
								"payment_terms",
								"bottom_text",
								"quote_labour_total",
								"pretax_total",
								"taxable_total"
							]

							copyFields.forEach(field => {
								quotePayload[field] = responses[1].data[field]
							})

							vm.postData("ServiceInvoice/save",quotePayload,true).then(quoteSaveResponse => {

								if (quoteSaveResponse.success) {
									let invoiceObjectsPayload = {
										records: JSON.stringify(responses[2].data.map(obj => {

											//sanities field_values_json
											let fields = JSON.parse(obj.field_values_json)

											delete fields.invoice_id
											delete fields.selected
											delete fields.rowClasses

											return {
												invoice_id: quoteSaveResponse.data.record_id,
												type: obj.type,
												field_values_json: JSON.stringify(fields)
											}
										}))
									}

									vm.postData("ServiceInvoiceObject/save-batch",invoiceObjectsPayload).then(finalResponse => {
										vm.spinner(false)

										if (finalResponse.success) {
											vm.alertSaved("Quote cloned!")
											vm.$router.push("/service/"+responses[0].data.slug+"/quote")
										}
									})
								}
								else 
									vm.spinner(false)
							})
						}
						else 
							vm.spinner(false)
					})
				}
			})
		},
		materialLabourChange(total) {
			this.materialLabourTotal = total
		},
		togglePreviewSize() {
			this.previewExpanded = !this.previewExpanded
			this.previewRefresher++
		},
		attachmentSelectionChange(files) {
			this.selectedFiles = files
		},
		activateSection(item) {
			this.activeSection = item
		},
		prevStep() {
			//find index of current and minus one
			this.activeSection = this.invoiceSections[this.invoiceSections.indexOf(this.activeSection)-1]
		},
		nextStep() {
			//find index of current and minus one
			this.activeSection = this.invoiceSections[this.invoiceSections.indexOf(this.activeSection)+1]
		},
		deleteInvoice() {
			this.deleteInvoiceConfirmModalVisible = true
		},
		confirmDeleteInvoice() {
			this.deleteInvoiceConfirmModalVisible = false

			let vm = this

			this.postData("ServiceInvoice/delete",{id: this.invoiceId}).then(response => {
				if (response.success) {
					vm.alertSuccess("Invoice Deleted")
					vm.deleteInvoiceConfirmModalVisible = false

					if (vm.isForSingleService)
						vm.$router.push("/service/"+vm.$store.service.slug+"/dashboard")
					else
						vm.$router.push("/financial/invoices")
				}
			})			
		},
		makePartialPayment() {
			this.partialPaymentModal.visible = true
			this.partialPaymentModal.payment_amount = 0
		},
		confirmPartialPayment() {
			let vm = this

			let payload = {
				id: this.invoiceDetails.id,
				payment_amount: this.partialPaymentModal.payment_amount
			}

			this.partialPaymentModal.working = true

			this.postData("ServiceInvoice/partial-payment",payload,true).then(response => {
				vm.partialPaymentModal.working = false
				if (response.success) {
					vm.partialPaymentModal.visible = false
					vm.alertSuccess("Partial payment recorded")
					vm.previewRefresher++
					vm.fetchInvoiceData()
				}
			})
		},
		recordFullPayment() {
			let vm = this

			let payload = {
				id: this.invoiceDetails.id,
				payment_amount: this.invoiceDetails.grand_total - this.invoiceDetails.amount_paid
			}

			this.postData("ServiceInvoice/partial-payment",payload,true).then(response => {
				if (response.success) {
					vm.alertSuccess("Invoice recorded as paid in full")
					vm.previewRefresher++
					vm.fetchInvoiceData()
				}
			})
		},
		updateAmountDue() {
			this.updateAmountDueModal.visible = true
			this.updateAmountDueModal.amount_due = this.balanceOwing.toFixed(2)
		},
		confirmUpdateAmountDue() {
			let vm = this

			let amount_due = this.newAmountDue

			let payload = {
				id: this.invoiceDetails.id,
				amount_due,
				skipQBOPush: true
			}

			this.postData("ServiceInvoice/save",payload,true).then(response => {
				if (response.success) {
					vm.alertSaved("Total Requested updated to $"+amount_due.toCurrency())
					vm.updateAmountDueModal.visible = false
					vm.previewRefresher++
					vm.fetchInvoiceData()
				}
			})
		},
		compileWorksheets(services) {
			let vm = this
			let mergedWorksheets = []

			let additional = []
			services.forEach(service => {

				if ("ServiceWorksheet" in service && service.ServiceWorksheet.length > 0) {
					mergedWorksheets = mergedWorksheets.concat(vm.joinWorksheets(
						service.ServiceWorksheet,
						service.job_num
					))
				}
				else {
					mergedWorksheets = mergedWorksheets.concat(vm.joinWorksheets([
						{
							parts_used_json: service.parts_used_json,
							tasks_completed_json: service.tasks_completed_json,
							equipment_used_json: service.equipment_used_json,
							work_completed_description: service.work_completed_description,
							service_tasks_completed_json: service.service_tasks_completed_json,
							created_at: service.work_completed_at
						}
					],service.job_num))
				}
			})

			let tasks = []
			let equipment = []
			let materials = []
			let serviceTasks = []
			let workCompleted = []

			mergedWorksheets.forEach(obj => {
				let entryDate = vm.moment(obj.created_at).format("MMM DD")

				tasks = tasks.concat(obj.tasks)
				equipment = equipment.concat(obj.equipment)
				materials = materials.concat(obj.materials)
				serviceTasks = serviceTasks.concat(obj.serviceTasks)

				workCompleted.push(obj.description)
			})

			let compiledDescription = ""

			if (services.length > 0)
				compiledDescription = workCompleted.join('<hr>')

			//Set compiled objects into an object
			let compiled = {
				tasks,
				equipment,
				materials: vm.normalizeMaterials(materials),
				serviceTasks,
				description: compiledDescription,
				additional
			}

			return compiled
		},
		/**
		* Used in labour mutation (mutatedLabour)
		* looks for matching labour line item type/rate, and merges the addition to that, otherwise adds a new line
		**/
		pushToLabour(labour,obj) {
			let vm = this

			let found = false
			labour.forEach(labourEntry => {
				if (labourEntry.chargeOutGroupName == obj.chargeOutGroupName && labourEntry.rate == obj.rate) {
					let hrs = parseFloat(labourEntry.hrs) + parseFloat(obj.hrs)
					labourEntry.hrs = hrs.toFixed(2)

					let total = labourEntry.hrs * parseFloat(labourEntry.rate)
					labourEntry.total = total.toFixed(2)

					found = true
				}
			})
			if (!found) 
				labour.push(obj)

			return labour
		},
		regenInvoice() {
			this.generatingInvoice = true
			this.regenerateConfirmModalVisible = false
			this.invoiceExists = false

			let vm = this

			vm.generatingInvoiceMsg = "Deleting current data..."

			let promises = [
				this.fetchData(),
				this.postData("ServiceInvoice/delete",{id:vm.invoiceDetails.id})
			]

			Promise.all(promises).then(responses => {
				vm.generateInvoice().then(resolve => {
					let params = {}

					let invoiceSlug = null
					if ("response" in resolve) {						invoiceSlug = resolve.response.data.slug

						if (vm.isForSingleService) {
							params.filters = [
								["service_id",vm.invoiceDetailsModel.services]
							]

							vm.getJSON("ServiceInvoice/get",params).then(response => {
								if (response.success)
									vm.$emit("setInvoices",response.data)
							})
						}
						else {
							//get invoices
							params.filters = [
								["invoice_id",resolve.response.record_id]
							]

							vm.getJSON("Service/get",params).then(response => {
								
								if (response.success)
									vm.invoiceDetailsModel.services = response.data.map(service => service.id)

								vm.$emit("invoiceSlugChange",invoiceSlug)
							})
						}
					}
					else 
						vm.alertFail("Error -- service not in memory. Refresh and try again")
				})
			})
		},
		confirmRegenSection(section) {
			this.regenerateSectionConfirmModal.sectionName = section
			this.regenerateSectionConfirmModal.visible = true

			let functionName = "regenerate"+section+"Section"
			this.regenerateSectionConfirmModal.functionName = functionName

		},
		executeRegenSection() {
			this[this.regenerateSectionConfirmModal.functionName]()
			this.regenerateSectionConfirmModal.visible = false
		},
		regenerateEquipmentSection() {
			let vm = this

			if (this.acceptedQuote && this.quoteRecord.is_change_quote == 0) {
				this.invoiceRegenSourceModal.visible = true
				this.invoiceRegenSourceModal.section = "Equipment"
			}
			else 
				this.generateInvoiceSectionFromWorksheets("Equipment")

			/*let promises = []
			if (this.isForSingleService) 
				promises.push(this.fetchService())
			else 
				promises.push(this.fetchInvoiceServices({invoice_id:this.invoiceId}))

			//delete objects in materials table
			this.invoiceEquipment.forEach(item => {
				promises.push(this.postData("ServiceInvoiceObject/delete",{id: item.id}))
			})

			vm.spinner(true)

			//vm.alertInfo("Deleting current data...",true)

			Promise.all(promises).then(responses => {
				//now pull in the records again

				//compile worksheets into one
				let compiled = vm.compileWorksheets(vm.isForSingleService ? [responses[0].data] : responses[0].data)

				let eqSet = vm.generateInvoiceSection(compiled.equipment,"Equipment",vm.invoiceDetails.id)
				
				vm.regenerateSave(eqSet,"Equipment")
			})*/
		},
		regenerateTaskSection() {
			if (this.acceptedQuote && this.acceptedQuote.is_change_quote == 0) {
				this.invoiceRegenSourceModal.visible = true
				this.invoiceRegenSourceModal.section = 'Task'
			}
			else 
				this.generateInvoiceSectionFromWorksheets("Task")
		},
		regenerateLabourSection() {
			if (this.acceptedQuote && this.acceptedQuote.is_change_quote == 0) {
				this.invoiceRegenSourceModal.visible = true
				this.invoiceRegenSourceModal.section = 'Labour'
			}
			else 
				this.generateInvoiceSectionFromWorksheets("Labour")
		},
		regenerateMaterialSection() {
			if (this.acceptedQuote && this.acceptedQuote.is_change_quote == 0) {
				this.invoiceRegenSourceModal.visible = true
				this.invoiceRegenSourceModal.section = 'Material'
			}
			else 
				this.generateInvoiceSectionFromWorksheets("Material")
		},
		copyQuoteObjectsToInvoice(quote,section,invoice_id,invoice_slug) {
			let vm = this

			return new Promise((resolve,reject) => {

				let objects = quote.ServiceInvoiceObject

				if (section != 'All') 
					objects = objects.filter(obj => obj.type == section)

				let objectsPayload = {
					records: JSON.stringify(objects.map(obj => {

						//if labour, convert to property hrs, by multiplying crew
						if (obj.type == 'Labour') {
							try {
								let fieldValues = JSON.parse(obj.field_values_json)

								fieldValues.hrs = +fieldValues.hrs * +fieldValues.crew 

								delete fieldValues.crew

								obj.field_values_json = JSON.stringify(fieldValues)
							}
							catch (e) {}
						}

						return {
							type: obj.type,
							field_values_json: obj.field_values_json,
							invoice_id
						}
					}))
				}

				vm.postData("ServiceInvoiceObject/save-batch",objectsPayload).then(objSaveResponse => {
					if (objSaveResponse.success) {

						/*************************
						* Finally fetch the invoice data
						*************************/

						vm.fetchInvoiceData(false,invoice_id,invoice_slug).then(response => {

							vm.invoiceGenerationComplete = true

							vm.invoiceRegenSourceModal.visible = false

							vm.generatingInvoice = false
							vm.invoiceExists = true
							
							vm.alertSuccess("Invoice ready for editing")

							resolve({response:objSaveResponse})
						})
					}
					else
						resolve({response:objSaveResponse})
				})
			})
		},
		generateInvoiceSectionFromQuote(quote,section) {
			let vm = this
			//get a full copy of quote and its objects
			let params = {
				include: ["ServiceInvoiceObject"]
			}

			return new Promise((resolve,reject) => {

				vm.getJSON("ServiceInvoice/find/"+quote.slug,params,true).then(response => {
					if (response.success) {
						quote = response.data

						if (section == 'All') {

							let copyFields = ["grand_total","pretax_total","service_id","settings_override_json","taxable_total","untaxed_total"]

							let payload = {}

							copyFields.forEach(field => {
								payload[field] = quote[field]
							})

							payload.work_completed_description = "As per quote #Q"+vm.$store.service.job_num
							payload.customer_id = vm.$store.service.customer_id

							payload.applied_sales_tax_json = vm.compileDefaultTaxes()

							vm.postData("ServiceInvoice/save",payload,true).then(saveResponse => {
								if (saveResponse.success) {

									if ("slug" in saveResponse) 
										vm.$emit("invoiceSlugChange",saveResponse.slug)

									vm.deleteSectionContents(section)

									vm.copyQuoteObjectsToInvoice(quote,section,saveResponse.data.record_id,saveResponse.data.slug).then(objSaveResponse => {
										vm.invoiceRegenSourceModal.visible = false
										resolve({response: objSaveResponse})
									})
								}
							})
						}
						else {
							vm.deleteSectionContents(section)

							vm.copyQuoteObjectsToInvoice(quote,section,vm.invoiceDetails.id,vm.invoiceDetails.slug).then(objSaveResponse => {
								vm.invoiceRegenSourceModal.visible = false
								resolve({response: objSaveResponse})
							})
						}
					}
				})
			})
		},
		deleteSectionContents(section) {

			let promises = []

			if (section == 'Material' || section == 'All') {
				this.invoiceMaterials.forEach(item => {
					promises.push(this.postData("ServiceInvoiceObject/delete",{id: item.id}))
				})

				this.assemblies.forEach(ass => {
					promises.push(this.postData("ServiceInvoiceObject/delete",{id: ass.id}))
				})
			}
			if (section == 'Equipment' || section == 'All') {
				this.invoiceEquipment.forEach(item => {
					promises.push(this.postData("ServiceInvoiceObject/delete",{id: item.id}))
				})
			}
			if (section == 'Labour' || section == 'All') {
				this.invoiceLabour.forEach(item => {
					promises.push(this.postData("ServiceInvoiceObject/delete",{id: item.id}))
				})
			}
			if (section == 'Task' || section == 'All') {
				this.invoiceTasks.forEach(item => {
					promises.push(this.postData("ServiceInvoiceObject/delete",{id: item.id}))
				})
			}
		},
		generateInvoiceSectionFromWorksheets(section) {
			let vm = this

			let promises = []
			if (this.isForSingleService) 
				promises.push(this.fetchService())
			else 
				promises.push(this.fetchInvoiceServices({invoice_id:this.invoiceId}))

			//delete existing objects
			this.deleteSectionContents(section)

			vm.spinner(true)

			Promise.all(promises).then(responses => {
				//now pull in the records again
				let compiled = vm.compileWorksheets(vm.isForSingleService ? [responses[0].data] : responses[0].data)

				let dataSet
				if (section == "Material") {
					compiled.materials = vm.normalizeMaterials(compiled.materials)

					vm.addMaterials(compiled.materials).then(response => {
						vm.fetchInvoiceData()
					})
				}
				else if (section == 'Equipment') {
					dataSet = vm.generateInvoiceSection(compiled.equipment,"Equipment",vm.invoiceDetails.id)

					vm.regenerateSave(dataSet,section)
				}
				else if (section == 'Labour') {
					let promises = [
						vm.getJSON("TimeLog/get",this.timeLogParams), //get the time logs on the service
						vm.fetchData()
					]

					vm.spinner(true)

					Promise.all(promises).then(responses => {
						//now pull in the records again

						let labourSet = vm.generateLabourSection(responses[0].data,vm.invoiceDetails.id)

						vm.regenerateSave(labourSet,"Labour")
					})
				}
				else if (section == 'Task') {
					let promises = []
					if (this.isForSingleService) 
						promises.push(this.fetchService())
					else 
						promises.push(this.fetchInvoiceServices({invoice_id:this.invoiceId}))

					vm.spinner(true)

					let taskType = "Task"
					let taskKey = "tasks"
					if (this.activeSection == 'Service Tasks') 
						taskKey = "serviceTasks"

					Promise.all(promises).then(responses => {
						//now pull in the records again

						let compiled = vm.compileWorksheets(vm.isForSingleService ? [responses[0].data] : responses[0].data)

						let tasksSet = vm.generateInvoiceSection(compiled[taskKey],taskType,vm.invoiceDetails.id)

						vm.regenerateSave(tasksSet,taskType)
					})
				}

				vm.invoiceRegenSourceModal.visible = false
			})
		},
		regenerateSave(recordSet,sectionName) {
			let vm = this

			return new Promise((resolve,reject) => {

				vm.postData("ServiceInvoiceObject/save-batch",{records: recordSet}).then(response => {

					vm.fetchInvoiceData().then(responses => {

						vm.alertSuccess(sectionName + " section regenerated")
						vm.spinner(false)

						vm.previewRefresher++

						vm.invoiceRegenSourceModal.visible = false

						resolve(response)
					}).catch(response => {})
				})

			})
		},
		generateInvoiceSection(source_json_or_object,type,invoice_id) {
			let obj = [], vm = this

			if (typeof source_json_or_object == 'string') {
				try {
					obj = JSON.parse(source_json_or_object)
				}
				catch (e) {}
			}
			else
				obj = source_json_or_object

			let defaultMarkup = 1, defaultPriceAdjustment = 1

			if (type == 'Material') {

				if (vm.clientInvoiceSettings.usePriceAdjustment && +vm.clientInvoiceSettings.default_price_adjustment != 0) 
					defaultPriceAdjustment = +vm.clientInvoiceSettings.default_price_adjustment
				else 
					//check for global settings
					defaultPriceAdjustment = vm.getDefaultPriceSetting("Material","price_adjustment")
				

				if (vm.clientInvoiceSettings.useMarkup && +vm.clientInvoiceSettings.default_markup != 0) 
					defaultMarkup = +vm.clientInvoiceSettings.default_markup	
				else 
					//check for global settings
					defaultMarkup = vm.getDefaultPriceSetting("Material","markup")
			}

			if (type == 'Equipment') {
				let typesUsed = []

				obj.forEach(item => {
					let foundIndex = typesUsed.findIndex(targetType => {
						if ("type_id" in item) {
							return targetType.equipment_type_id == item.type_id
						}
						else if ("description" in item) {
							return item.description == targetType.typeName
						}
					})

					if (foundIndex >= 0) {
						typesUsed[foundIndex].qty += +item.qty
					}
					else {
						let typeObj = {
							rate: 0
						}

						if ("EquipmentType" in item && item.EquipmentType) {
							typeObj.equipment_type_id = item.EquipmentType.id
							typeObj.charge_out_rates_json = item.EquipmentType.charge_out_rates_json
							typeObj.typeName = item.EquipmentType.name

							let unit = "hour"
							if ("qty_unit" in item && item.qty_unit)
								unit = item.qty_unit
							else if ("unit" in item && item.unit)
								unit = item.unit

							let resolved = vm.resolveEquipmentChargeOutRate(
								item.EquipmentType.charge_out_rate,
								item.EquipmentType.charge_out_rates_json,
								unit
							)
							typeObj.rate = resolved.rate
						}
						else {
							typeObj.typeName = item.description

							if ("rate" in item)
								typeObj.rate = item.rate
						}

						if ("qty" in item)
							typeObj.qty = +item.qty

						if ("qty_unit" in item)
							typeObj.qty_unit = item.qty_unit

						typesUsed.push(typeObj)
					}
				})

				return typesUsed.map(type => {
					return {
						invoice_id,
						type: "Equipment",
						field_values_json: JSON.stringify(type)
					}
				})
			}
			else {
				return obj.map(item => {

					//apply default markups, price adjustments

					let realType = type

					if (type == "Material") {
						item.priceAdjustment = defaultPriceAdjustment
						item.markup = defaultMarkup

						if ("itemType" in item && item.itemType == 'assembly')
							realType = "Assembly"
					}

					return {
						invoice_id,
						type: realType,
						field_values_json: JSON.stringify(item)
					}
				})
			}
		},
		generateLabourSection(timeLogs,invoice_id) {

			let vm = this

			let labour = []
			try {labour = JSON.parse(JSON.stringify(timeLogs))}
			catch (e) {}

			let mutatedLabour = []

			labour.forEach(log => {

				let type = ""
				let rate = 0
				let otRate15x = 0
				let otRate2x = 0
				let otRate25x = 0
				let hrs = 0

				if ( log.User.EmployeeClassification.length > 0 && "ClassificationGroup" in log.User.EmployeeClassification[0] && log.User.EmployeeClassification[0].ClassificationGroup) {

					let classGroup = log.User.EmployeeClassification[0].ClassificationGroup
					
					type = classGroup.label
					rate = parseFloat(classGroup.charge_out_rate)

					if (!isNaN(classGroup.ot_charge_out_rate))
						otRate15x = +classGroup.ot_charge_out_rate

					if (!isNaN(classGroup.ot2x_charge_out_rate))
						otRate2x = +classGroup.ot2x_charge_out_rate

					if (!isNaN(classGroup.ot25x_charge_out_rate))
						otRate25x = +classGroup.ot25x_charge_out_rate

					//check classification group overrides in customer invoice settings
					vm.clientRateOverrides.forEach(group => {
						if (group.group_id == classGroup.id) {

							if ("override" in group && group.override == true) {

								if ("newRateRegular" in group)
									rate =  +group.newRateRegular

								if ("newRateOT15x" in group)
									otRate15x = group.newRateOT15x
								else if ("newRateOT" in group) //legacy handling
									otRate15x = group.newRateOT

								if ("newRateOT2x" in group)
									otRate2x = group.newRateOT2x

								if ("newRateOT25x" in group)
									otRate25x = group.newRateOT25x
							}
						}
					})
				}
				else if (log.User.EmployeeClassification.length > 0) 
					type = log.User.EmployeeClassification[0].title

				rate = +rate
				otRate15x = +otRate15x
				otRate2x = +otRate2x
				otRate25x = +otRate25x

				if (parseFloat(log.regular_hours) > 0) {

					hrs = parseFloat(log.regular_hours)

					let total = rate*hrs

					mutatedLabour = vm.pushToLabour(mutatedLabour,{
						invoice_id: invoice_id,
						type: "Labour",
						chargeOutGroupName: type,
						rate: rate.toFixed(2),
						hrs: hrs.toFixed(2),
						total: total.toFixed(2)
					})
				}
				if (parseFloat(log.overtime_hours_15x) > 0) {

					hrs = parseFloat(log.overtime_hours_15x)

					let total = otRate15x*hrs

					mutatedLabour = vm.pushToLabour(mutatedLabour,{
						invoice_id: invoice_id,
						type: "Labour",
						chargeOutGroupName: type + " (overtime 1.5x)",
						rate: otRate15x.toFixed(2),
						hrs: hrs.toFixed(2),
						total: total.toFixed(2)
					})
				}
				if (parseFloat(log.overtime_hours_2x) > 0) {

					hrs = parseFloat(log.overtime_hours_2x)

					let total = otRate2x * hrs

					mutatedLabour = vm.pushToLabour(mutatedLabour,{
						invoice_id: invoice_id,
						type: "Labour",
						chargeOutGroupName: type + " (overtime 2x)",
						rate: otRate2x.toFixed(2),
						hrs: hrs.toFixed(2),
						total: total.toFixed(2)
					})
				}
				if (parseFloat(log.overtime_hours_25x) > 0) {

					hrs = parseFloat(log.overtime_hours_25x)

					let total = otRate25x * hrs

					mutatedLabour = vm.pushToLabour(mutatedLabour,{
						invoice_id: invoice_id,
						type: "Labour",
						chargeOutGroupName: type + " (overtime 2.5x)",
						rate: otRate25x.toFixed(2),
						hrs: hrs.toFixed(2),
						total: total.toFixed(2)
					})
				}
			})

			return mutatedLabour.map(log => {
				return {
					invoice_id: invoice_id,
					type: "Labour",
					field_values_json: JSON.stringify(log)
				}
			})
		},
		clickGenerateInvoice(event) {
			//discard event
			let vm = this
			this.generateInvoice().then(response => {
				if ("fromQuote" in event && event.fromQuote) {

					localStorage.setItem("togglePartialPayment",true)
					vm.$router.push("/service/"+vm.$store.service.slug+"/invoice/Finalizing")
				}
			})
		},
		generateInvoice(noServices,invoiceId) {
			let vm = this

			let invoicePayload = {}

			if (invoiceId)
				invoicePayload.id = invoiceId

			vm.generatingInvoice = true
			vm.generatingInvoiceMsg = "Creating an invoice record..."

			//check if Accepted quote exists on service, if so, just copy the quote
			if (this.acceptedQuote && this.acceptedQuote.is_change_quote == 0) {

				return new Promise((resolve,reject) => {
					vm.generateInvoiceSectionFromQuote(vm.acceptedQuote,'All').then(response => {
						resolve(response)
					})
				})
				
			}
			else {

				return new Promise((resolve,reject) => {

					//need to make sure we have the service id, and a fetch may be required for that
					let getServiceIdPromise = new Promise((resolve,reject) => {
						if (vm.invoiceDetailsModel.services.length == 1) 
							resolve(vm.invoiceDetailsModel.services[0])
						else if (vm.isForSingleService) {
							if ("id" in vm.$store.service)
								resolve(vm.$store.service.id)
							else {
								vm.fetchService().then(response => {
									if (response.success)
										resolve(response.data.id)
									else {
										if ("id" in vm.$store.service)
											resolve(vm.$store.service.id)
										else 
											resolve(false)
									}
								})
							}
						}
						else 
							resolve(0)
					})

					getServiceIdPromise.then(serviceId => {
						invoicePayload.service_id = serviceId

						invoicePayload.applied_sales_tax_json = vm.compileDefaultTaxes()

						let promises = [
							vm.postData("ServiceInvoice/save",invoicePayload), //create the Invoice record
							vm.getJSON("TimeLog/get",this.timeLogParams) //get the time logs on the service		
						]

						if (!noServices) {
							let serviceParams = {
								fields: ["id","slug","job_num","job_name","work_completed_description","parts_used_json","equipment_used_json","tasks_completed_json","customer_id","permit_needed","permit_cost","permit_notification_number"],
								include: [
									{
										model:"ServiceWorksheet",
										include: [{model: "CustomerAsset",fields:["description","serial_num"]}]
									},
									{
										model: "ServiceInvoice",
										fields: ["is_quote","is_change_quote","pretax_total"]
									}
								]
							}

							if (vm.invoiceDetailsModel.services.length > 0)
								serviceParams.filters = [["id",vm.invoiceDetailsModel.services]]
							else
								serviceParams.filters = [["id",vm.$store.service.id]]

							promises.push(vm.getJSON("Service/get",serviceParams))
						}

						Promise.all(promises).then(responses => {

							vm.generatingInvoiceMsg = "Populating with data..."

							let invoiceResponse = responses[0]
							let timeLogs = responses[1].data

							let promises2 = []

							if (!noServices) {
								let invoiceServices = responses[2].data

								//Compile worksheets into appropriate invoice sections
								let compiled = vm.compileWorksheets(invoiceServices)

								//check if permit costs need to be added to compiled additional charges
								invoiceServices.forEach(service => {
									//Add permits as "additional" invoice section type
									if (service.permit_needed == 1 && service.permit_cost && +service.permit_cost != 0) {
										compiled.additional.push({
											type: "Additional",
											invoice_id: invoiceResponse.record_id,
											field_values_json: JSON.stringify({
												description: "Permit #"+service.permit_notification_number,
												price: service.permit_cost,
												isTaxable: true
											})
										})
									}

									//add a changeq quote as an additional charge
									if ("ServiceInvoice" in service && service.ServiceInvoice) {
										
										let changeQuote = service.ServiceInvoice.find(inv => inv.is_change_quote == 1)

										if (changeQuote) {
											compiled.additional.push({
												type: "Additional",
												invoice_id: invoiceResponse.record_id,
												field_values_json: JSON.stringify({
													description: "Changes as Quoted",
													price: changeQuote.pretax_total,
													isTaxable: true
												})
											})
										}
									}
								})

								/*************************
								* Generate invoice material/tasks/equipment/additional/labour type sections, all merged into the same array
								*************************/

								//console.log(vm.generateInvoiceSection(compiled.equipment,"Equipment",invoiceResponse.record_id))

								let expandedMaterials = vm.expandMaterials(compiled.materials)

								let expandedWithAssemblies = [
									...expandedMaterials.expandedMaterials,
									...expandedMaterials.assemblies
								]

								let recordsSet = JSON.stringify([
									...vm.generateInvoiceSection(expandedWithAssemblies,"Material",invoiceResponse.record_id),
									...vm.generateInvoiceSection(compiled.equipment,"Equipment",invoiceResponse.record_id),
									...vm.generateInvoiceSection(compiled.tasks,"Task",invoiceResponse.record_id),
									...vm.generateInvoiceSection(compiled.serviceTasks,"Task",invoiceResponse.record_id),
									...vm.generateLabourSection(timeLogs,invoiceResponse.record_id),
									...compiled.additional
								])

								promises2 = [
									vm.postData("ServiceInvoiceObject/save-batch",{records:recordsSet}),
								]

								let serviceInvoicePayload = {id: invoiceResponse.record_id,work_completed_description: compiled.description}
								let servicePayload = {}

								if (invoiceServices.length > 1) {
									//this is a batch invoice, so set the invoice_id on the services
									serviceInvoicePayload.service_id = 0
									servicePayload.invoice_id = invoiceResponse.record_id
								}
								else if (invoiceServices.length == 1) {
									//this is a single service invoice, so set the service_id on the invoice
									serviceInvoicePayload.service_id = invoiceServices[0].id
									servicePayload.invoice_id = 0
								}

								promises2.push(vm.postData("ServiceInvoice/save",serviceInvoicePayload))

								invoiceServices.forEach(service => {
									servicePayload.id = service.id
									promises2.push(vm.postData("Service/save",servicePayload))
								})
							}

							Promise.all(promises2).then(responses => {

								/*************************
								* Finally fetch the invoice data
								*************************/

								vm.fetchInvoiceData(false,invoiceResponse.record_id,invoiceResponse.slug).then(response => {

									vm.invoiceGenerationComplete = true

									vm.generatingInvoice = false
									vm.invoiceExists = true

									vm.previewRefresher++

									if ("slug" in invoiceResponse)
										vm.$emit("invoiceSlugChange",invoiceResponse.slug)
									
									vm.alertSuccess("Invoice ready for editing")

									resolve({response:invoiceResponse})
								})
							})
						})
					})
				})
			}
		},
		copyFromQuoteTemplate() {
			this.templatesModalVisible = true
		},
		pickTemplateToCopy(fullTemplate) {
			let vm = this

			vm.selectedTemplateToCopy = fullTemplate
			vm.templatesModalVisible = false

			vm.generatingInvoice = true
			vm.generatingInvoiceMsg = "Creating a quote from template..."

			let payload = {
				is_quote: 1
			}

			if (vm.invoiceServices.length > 0)
				payload.service_id = vm.invoiceServices[0].id
			else
				payload.service_id = vm.$store.service.id

			if (fullTemplate.work_to_do)
				payload.work_completed_description = fullTemplate.work_to_do

			payload.applied_sales_tax_json = vm.compileDefaultTaxes()

			vm.postData("ServiceInvoice/save",payload,true).then(response => {
				if (response.success) {
					let records = vm.buildServiceInvoiceObjectRecordsFromTemplate(fullTemplate,response.data.record_id)

					let objectsPayload = {
						records: JSON.stringify(records)
					}

					vm.postData("ServiceInvoiceObject/save-batch",objectsPayload,true).then(objSaveResponse => {
						vm.alertSuccess("Quote created from template")

						vm.generatingInvoice = false
						vm.invoiceGenerationComplete = true
						vm.invoiceExists = true

						vm.$emit("setInvoices",[{
							id: response.data.record_id,
							slug: response.data.slug,
							is_quote: 1
						}])

						vm.$emit("invoiceSlugChange",response.data.slug)

						vm.fetchInvoiceData(false,false,response.data.slug)
					})
				}
				else
					vm.generatingInvoice = false
			})
		},
		createQuote() {
			let vm = this

			vm.generatingInvoice = true
			vm.generatingInvoiceMsg = "Creating a quote..."

			let payload = {
				is_quote: 1
			}

			if (vm.invoiceServices.length > 0) {
				payload.service_id = vm.invoiceServices[0].id,
				payload.work_completed_description = vm.invoiceServices[0].description
			}
			else {
				payload.service_id = this.$store.service.id
				payload.work_completed_description = this.$store.service.description
			}

			payload.applied_sales_tax_json = vm.compileDefaultTaxes()

			this.postData("ServiceInvoice/save",payload,true).then(response => {
				if (response.success) {
					vm.alertSuccess("Quote ready for editing")

					vm.generatingInvoice = false
					vm.invoiceGenerationComplete = true
					vm.invoiceExists = true

					vm.$emit("setInvoices",[{
						id: response.data.record_id,
						slug: response.data.slug,
						is_quote: 1
					}])

					vm.$emit("invoiceSlugChange",response.data.slug)

					vm.fetchInvoiceData(false,false,response.data.slug)
				}
			})
		},
		salesTaxToggle(invoiceDetails) {
			//only save if its already been saved
			let vm = this
			if ("id" in invoiceDetails) {

				let payload = {
					id: invoiceDetails.id,
					applied_sales_tax_json: invoiceDetails.applied_sales_tax_json,
					skipQBOPush: true
				}

				this.postData("ServiceInvoice/save",payload,true).then(response => {
					if (response.success) {
						vm.previewRefresher++
						vm.fetchInvoiceData()
					}
				})
			}
		},
		executeSaveInvoice(updateQBO) {
			let vm = this

			let payload = {
				work_completed_description: this.invoiceDetails.work_completed_description,
				customer_notes: this.invoiceDetails.customer_notes,
				issue_date: this.invoiceDetails.issue_date,
				finalized: this.invoiceDetails.finalized,
				payment_terms: this.invoiceDetails.payment_terms,
				bottom_text: this.invoiceDetails.bottom_text,
				attn: this.invoiceDetails.attn,
				amount_due: this.invoiceDetails.amount_due,
				applied_sales_tax_json: this.invoiceDetails.applied_sales_tax_json,
				manual_invoice_number: this.invoiceDetails.manual_invoice_number,
				exchange_rate: this.invoiceDetails.exchange_rate,
				quote_followup_date: this.invoiceDetails.quote_followup_date
			}

			if (this.invoiceSlug && this.invoiceSlug != "new")
				payload.slug = this.invoiceSlug
			else if ("id" in this.invoiceDetails && this.invoiceDetails.id)
				payload.id = this.invoiceDetails.id

			if (this.invoiceDetailsModel.services.length > 0)
				payload.is_freeform = 0
			else if (this.invoiceDetailsModel.is_freeform == 1)
				payload.is_freeform = 1
			else 
				payload.is_freeform = 0

			if (this.invoiceDetailsModel.customer_id > 0)
				payload.customer_id = this.invoiceDetailsModel.customer_id

			if (this.isQuote)
				payload.is_quote = 1
			else
				payload.is_quote = 0

			if (!updateQBO) {
				payload.skipQBOPush = true
			}

			//calculate due date
			let dueInDays = this.invoiceDetails.payment_terms

			let dueDate = this.moment(this.invoiceDetails.issue_date).add(dueInDays,"days")

			payload.due_date = dueDate.format("YYYY-MM-DD")

			this.detailsSaveWorking = true

			return new Promise((resolve,reject) => {

				vm.postData("ServiceInvoice/save",payload,true).then(response => {

					let initialSlug = vm.invoiceSlug

					if (!vm.isForSingleService) {
						let promises = []

						vm.invoiceDetailsModel.services.forEach(id => {
							promises.push(vm.postData("Service/save",{id:id,invoice_id:response.record_id}))
						})

						Promise.all(promises).then(responses => {
							if (initialSlug == 'new') {
								vm.generateInvoice(payload.is_freeform == 1 ? true : false,response.record_id)
							}
						})
					}

					if ("slug" in response && vm.invoiceSlug != response.slug) 
						vm.$emit("invoiceSlugChange",response.slug)

					vm.detailsSaveWorking = false
					if (response.success) {
						vm.previewRefresher++
						vm.alertSaved(vm.typeName+" details saved")
					}

					if (vm.invoiceDetailsModel.services.length != vm.invoiceServices.length){
						vm.fetchInvoiceServices({service_ids:this.invoiceDetailsModel.services})
					}

					resolve(response)
				})

			})

			this.previewRefresher++
		},
		saveInvoice(noServices) {
			let isUpdate = false, vm = this

			if (this.invoiceDetails.qbo_id > 0)
				isUpdate = true

			return new Promise((resolve,reject) => {
				if (vm.qboIsActive && isUpdate && this.invoiceDetails.finalized == 1) {
					this.openQboConfirmModal("executeSaveInvoice","finalizedSuccess")
					reject()
				}
				else {
					this.executeSaveInvoice(true).then(response => {
						resolve(response)
					})
				}
			})
		},
		mapInvoiceObjects(list) {
			return list.map(obj => {

				let fields = JSON.parse(obj.field_values_json)

				fields.id = obj.id
				fields.invoice_id = obj.invoice_id

				return fields
			})
		},
		toggleFinalizedStatus(state) { //true for finalizing, false for de-finalizing
			let vm = this

			if (state) {

				if (this.invoiceDetails.payment_terms === '') {
					vm.alertFail("Payment terms must be set") 
					vm.$store.errorList = {
						payment_terms: true
					}
				}
				else {
					this.invoiceDetails.finalized = 1

					//save invoice as well
					this.saveInvoice().then(response => {
						vm.finalizedSuccess(response)	
					})
				}

			}
			else { //DE-FINALIZE
				let payload = {
					id: this.invoiceDetails.id,
					skipQBOPush: true
				}

				payload.finalized = 0

				this.postData("ServiceInvoice/save",payload,true).then(response => {

					if (response.success) {
						vm.alertSuccess(vm.typeName+" de-finalized for editing")
						vm.invoiceDetails.finalized = payload.finalized
						//vm.activeSection = "Finalizing"
					}

				})
			}
		},
		finalizedSuccess(response) {
			let vm = this
			if (response && response.success) {
				vm.alertSuccess(vm.typeName+" finalized!")
				vm.activeSection = "Delivery"
			}
			else {
				if ("errors" in response)
					vm.$store.errorList = response.errors
				
				vm.invoiceDetails.finalized = 0
			}
		},
		bindSelectedFiles() {
			let vm = this

			let selectedFiles = []
			try {
				selectedFiles = JSON.parse(this.invoiceDetails.attached_files_json)
			}
			catch (ex) {}

			this.selectedFiles = []
			selectedFiles.forEach(fileId => {

				vm.invoiceServices.forEach(service => {
					if ("ServiceFile" in service) {

						service.ServiceFile.forEach(file => {

							if (file.id == fileId)
								vm.selectedFiles.push(fileId)
							
						})
					}
				})
			})
		},
		toggleProcessed(state) {
			let vm = this

			let payload = {
				id: this.invoiceDetails.id,
				skipQBOPush: true
			}

			if (state)
				payload.is_processed = 1
			else
				payload.is_processed = 0

			this.postData("ServiceInvoice/save",payload,true).then(response => {

				if (response.success) {

					if (state) 
						vm.alertSuccess("Invoice marked as Processed")
					
					else 
						vm.alertSuccess("Invoice marked as Un-Processed")

					vm.fetchInvoiceData()
				}
				
			})
		},
		setInvoiceObjects(objs) {
			let vm = this

			vm.invoiceMaterials = vm.mapInvoiceObjects(objs.filter(obj => obj.type == "Material"))

			vm.assemblies = vm.mapInvoiceObjects(objs.filter(obj => obj.type == "Assembly"))

			vm.invoiceLabour = vm.mapInvoiceObjects(objs.filter(obj => obj.type == "Labour"))
			vm.invoiceTasks = vm.mapInvoiceObjects(objs.filter(obj => obj.type == "Task"))
			vm.invoiceEquipment = vm.mapInvoiceObjects(objs.filter(obj => obj.type == "Equipment"))

			vm.invoiceAdditionalCharges = vm.mapInvoiceObjects(objs.filter(obj => obj.type == "Additional"))
		},
		fetchInvoiceData(spinner,invoiceId,invoiceSlug) {
			let vm = this

			let theInvoiceId = invoiceId, theInvoiceSlug = invoiceSlug

			if (!theInvoiceId)
				theInvoiceId = vm.invoiceId

			if (!theInvoiceSlug)
				theInvoiceSlug = vm.invoiceSlug

			return new Promise((resolve,reject) => {

				if (!theInvoiceSlug || theInvoiceSlug == 'new') {
					if (!vm.isForSingleService || vm.invoiceSlug || vm.intialStuffFetched)
						vm.showLoadingScreen = false
					resolve([])
				}
				else {
					if (spinner)
						vm.spinner(true)

					let invoiceParams = {
						include: [
							{
								model: "Contact",
								include: [
									{model:"Contact", fields:["id","slug","name","email"]}
								]
							},
							{
								model:"Service",
								fields: ["id","slug","job_name","job_num","Customer","invoice_id","customer_id"],
								include: [
									{model:"Contact",fields:["id","slug","name","settings_json"]},
									{model:"ServiceContract",fields:["id","slug"]},
									{
										model:"ServiceWorksheet",
										include: [{model: "CustomerAsset",fields:["description","serial_num"]}]
									},
									"ServiceFile"
								]
							},
							{
								model: "ServiceContract",
								fields: ["id","slug","contract_num","contract_description"]
							}
						]
					}

					let promises = [
						vm.getJSON("ServiceInvoice/find/"+theInvoiceSlug,invoiceParams)
					]

					if (theInvoiceId) {
						let invoiceObjParams = {
							filters: [["invoice_id",theInvoiceId]]
						}

						promises.push(vm.getJSON("ServiceInvoiceObject/get",invoiceObjParams),)
					}

					Promise.all(promises).then(responses => {
						vm.fetchHappened = true

						if (responses.length == 1 && responses[0].success) {
							//don't have id, we need to set it now and fetch the InvoiceObjects

							vm.invoiceExists = true

							let invoiceObjParams = {
								filters: [["invoice_id",responses[0].data.id]]
							}

							vm.getJSON("ServiceInvoiceObject/get",invoiceObjParams).then(response => {
								vm.showLoadingScreen = false
								if (response.success) {
									vm.setInvoiceObjects(response.data)
								}
							})
						}
						else if (responses.length > 1 && responses[1].success) {
							vm.showLoadingScreen = false
							vm.setInvoiceObjects(responses[1].data)
						}

						if (responses[0].success) {
							vm.setInvoiceDetails(responses[0].data)

							try {
								vm.clientInvoiceOverrides = JSON.parse(responses[0].data.settings_override_json)
								vm.setInvoiceDisplaySettings()
							}
							catch (e) {}

							vm.previewRefresher++

							vm.bindSelectedFiles()

							if (vm.invoiceDetails.finalized == 1)
								vm.activeSection = "Delivery"

							vm.invoiceDataIsSet = true

							if (vm.previewExpanded == null && vm.invoiceDetails.finalized == 1)
								vm.previewExpanded = true //default to large invoice view if invoice is finalized

							vm.showLoadingScreen = false

						}
						else {
							if ("errors" in responses[0] && !responses[0].errors.indexOf('Redundant request -- please wait for the first request to finish') >= 0) {
								//redundant request, just ignore
							}
							else
								vm.alertFail("Invoice not found")
							
							vm.showLoadingScreen = false
						}

						if (spinner)
							vm.spinner(false)

						resolve(responses)
					})
				}
			})
		},
		fetchInvoiceServices(options) {
			let vm = this

			let params = {
				include: [
					{
						model:"ServiceWorksheet",
						include: [{model: "CustomerAsset",fields:["description","serial_num"]}]
					},
					"ServiceFile"
				],
				fields: ["id","slug","job_num","job_name","work_completed_description","parts_used_json","equipment_used_json","tasks_completed_json","invoice_id","customer_id"],
				filters: false
			}

			return new Promise((resolve,reject) => {

				if ("invoice_id" in options) {
					params.filters = [
						["invoice_id",options.invoice_id]
					]
				}
				else if ("service_ids" in options && options.service_ids.length > 0) {
					params.filters = [
						["id",options.service_ids]
					]
				}

				if (params.filters && params.filters.length > 0) {
					vm.getJSON("Service/get",params).then(serviceResponse => {
						if (serviceResponse.success)  {
							if (serviceResponse.data.length > 0)
								vm.customer_id = serviceResponse.data[0].customer_id

							vm.invoiceServices = serviceResponse.data
							resolve(serviceResponse.data)
						}
						else
							resolve([])
					})
				}
				else
					resolve([])
			})
		},
		fetchInvoiceableServices() {
			let vm = this

			return new Promise((resolve,reject) => {

				let filters = [
					["status",["Processed","Completed","Reviewed"]],
					["archived",0]
				]

				if (vm.invoiceId)
					filters.push(["invoice_id",vm.invoiceId])
				else
					filters.push(["invoice_id",0])

				let invoiceableServiceParams = {
					include: [
						{model: "Contact",fields: ["id","slug","name"]},
						{model: "ServiceInvoice",fields:["id","slug","service_id","is_quote"],filters:[["is_quote",0]]}
					],
					filters,
					fields: ["id","slug","job_num","job_name","status","Customer","customer_id","invoice_id"]
				}

				//if services are already selected, constrain to the same customer
				if (vm.customer_id)
					invoiceableServiceParams.filters.push(["customer_id",vm.customer_id])

				vm.getJSON("Service/get",invoiceableServiceParams).then(response => {
					if (response.success)
						vm.invoiceableServices = response.data.filter(service => {
							if ("ServiceInvoice" in service && service.ServiceInvoice && service.ServiceInvoice.find(inv => inv.is_quote == 0))
								return false

							return true
						})

					resolve(response)
				})
			})
		},
		fetchCustomer() {
			let vm = this

			return new Promise((resolve,reject) => {
				let customerId = false

				if (vm.invoiceDetails.is_freeform == 1 && 'customer_id' in vm.invoiceDetails && vm.invoiceDetails.customer_id)
					customerId = vm.invoiceDetails.customer_id
				if (vm.isForSingleService && "customer_id" in vm.$store.service) 
					customerId = vm.$store.service.customer_id
				else if (vm.invoiceServices.length > 0)
					customerId = vm.invoiceServices[0].customer_id

				if (customerId) {
					this.getJSON("Contact/find/"+customerId,{include: ["Contact"]}).then(response => {
						vm.customerFetched = true
						vm.setCustomer(response.data)
						vm.setInvoiceDisplaySettings()

						resolve(response)
					})
				}
				else {
					vm.applyDefaultPaymentTerms()
					resolve({})
				}
			})
		},
		applyDefaultPaymentTerms() {
			if (!this.invoiceDetails.id)
				return
			if (!(this.invoiceDetails.payment_terms == null || this.invoiceDetails.payment_terms === ''))
				return

			let customerId = false

			if (this.invoiceDetails.is_freeform == 1 && this.invoiceDetails.customer_id)
				customerId = this.invoiceDetails.customer_id
			if (this.isForSingleService && this.$store.service && this.$store.service.customer_id)
				customerId = this.$store.service.customer_id
			else if (this.invoiceServices.length > 0 && this.invoiceServices[0] && this.invoiceServices[0].customer_id)
				customerId = this.invoiceServices[0].customer_id

			if (!customerId && this.invoiceDetails.Service && this.invoiceDetails.Service.customer_id)
				customerId = this.invoiceDetails.Service.customer_id
			if (!customerId && this.invoiceDetails.GroupServices && this.invoiceDetails.GroupServices.length > 0)
				customerId = this.invoiceDetails.GroupServices[0].customer_id

			// Customer terms override the fallback, so wait until that contact has been loaded.
			if (customerId && !this.customerFetched)
				return

			let terms = 30
			if (this.$store.company && this.$store.company.CompanySetting) {
				let globalTerms = this.$store.company.CompanySetting.invoice_payment_terms
				if (!(globalTerms == null || globalTerms === ''))
					terms = globalTerms
			}
			if (this.clientInvoiceSettings && !(this.clientInvoiceSettings.paymentTerms == null || this.clientInvoiceSettings.paymentTerms === ''))
				terms = this.clientInvoiceSettings.paymentTerms

			this.$set(this.invoiceDetails, 'payment_terms', terms)
		},
		setInvoiceDisplaySettings() {

			// Priority:   Company Settings < Customer Settings < Invoice Settings
			let settings = {}, vm = this

			function addSettings(new_settings) {
				Object.keys(new_settings).forEach(key => {
					if (key in new_settings)
						settings[key] = new_settings[key]
				})
			}

			if (this.$store.company && 'CompanySetting' in this.$store.company) {
				try {
					const companySettings = JSON.parse(this.$store.company.CompanySetting.job_defaults_json)
					addSettings(companySettings)
				} catch {}
			}

			if (this.customer && this.customer.settings_json) {
				try {
					const customerSettings = JSON.parse(this.customer.settings_json)
					const applyCustomerDisplay = customerSettings.override == 1
					const displayKeys = [
						"override",
						"detailedEquipment",
						"detailedLabour",
						"detailedAdditionalCharges",
						"lumpSum",
						"showWorkRequested",
						"materialsMarkup",
						"detailedMaterials",
						"materialsQTYOnly",
						"summaryMarkup",
						"breakdownAssemblies",
						"detailedProjectChangeOrders",
						"tasksDailyLog"
					]

					Object.keys(customerSettings).forEach(settingKey => {
						let isDisplayKey = displayKeys.indexOf(settingKey) >= 0
						if (settingKey.indexOf("materialColumn:") == 0)
							isDisplayKey = true

						if (isDisplayKey && !applyCustomerDisplay)
							return

						settings[settingKey] = customerSettings[settingKey]
					})
				} catch {}
			}

			if (this.clientInvoiceOverrides.override == 1) {
				let keysToRemove = [
					"useMarkup",
					"usePriceAdjustment",
					"attachDirectly",
					"default_markup",
					"default_price_adjustment",
					"chargeOutGroupOverrides"
				] //some defaults are not settable directly on the invoice

				keysToRemove.forEach(key => {
					delete vm.clientInvoiceOverrides[key]
				})

				addSettings(this.clientInvoiceOverrides)
			}

			this.clientInvoiceSettings = settings
			this.applyDefaultPaymentTerms()
		},
		fetchData(spinner) {
			let vm = this

			return new Promise((resolve,reject) => {

				if (spinner)
					vm.spinner(true)

				let promises = [
					this.fetchCompanyData(false,["ProfileImage","BusinessType","CompanySetting"]),
					this.getJSON("CollectiveAgreement/get",{include: ["Union"]})
				]

				let servicesQueryIndex = false

				Promise.all(promises).then(responses => {
					if (spinner)
						vm.spinner(false)

					vm.setInvoiceDisplaySettings()

					if (responses[1].success)
						vm.$store.collectiveAgreements = responses[1].data

					vm.dataFetched = true

					if (!vm.isForSingleService || vm.invoiceSlug || vm.intialStuffFetched)
						vm.showLoadingScreen = false

					resolve(responses)
				})

			})
		}
	},
	created() {
		let vm = this

		if ("tab" in this.$route.params && this.$route.params.slug != 'new')
			this.activeSection = this.$route.params.tab

		if (this.invoiceSlug == 'new') {
			this.showLoadingScreen = false
			this.invoiceExists = true
		}

		this.fetchInitialStuff()

		//we need to listen for scroll to properly size the fixed invoice preview window
		document.addEventListener("scroll",(e) => {
			if (document.documentElement.scrollTop > 50) 
				vm.scrolledBelowHeader = true
			else
				vm.scrolledBelowHeader = false
		})

		//hack to prevent stuttering of pdf preview due to it being pulled multiple times
		setTimeout(() => {
			vm.showPreview = true
		},2000)

	}
}
</script>

<style type="text/css" scoped>

	.icon-button {
		font-size: 16px;
	}
	.opacity-50 {
		opacity: 50%;
	}
	.integration-logo {
		height: 20px;
		margin-right: 20px;
	}

	.invoice-preview {
		width: 100%;
		min-height: 600px;
		border: 1px solid #e0e0e0;

	}

	.incomplete-alert, .worksheet-change-alert {
		margin-left: -15px;
		margin-right: -15px;
		margin-top: -10px;
		margin-bottom: 10px;
		border-radius: 0;
	}

	@media (max-width: 1200px) {

		.invoice-preview {
			width: 100%;
			height: calc(100% - 60px);
		}

		.incomplete-alert, .worksheet-change-alert {
			margin-top: -15px
		}
	}

	@media (max-width: 767px) {
		.incomplete-alert, .worksheet-change-alert {
			margin-top: -34px;
		}
	}
</style>